import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  completeMarketplacePasswordReset,
  validateMarketplacePasswordReset,
} from "../api/marketplaceAuth";
import { formatApiError } from "../utils/formatApiError";

export default function MarketplaceResetPassword() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();

  const [loading, setLoading] = useState(true);
  const [validInfo, setValidInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token) {
        setLoading(false);
        setValidInfo(null);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await validateMarketplacePasswordReset(token);
        if (!active) return;
        setValidInfo(data?.valid ? data : null);
      } catch (_err) {
        if (!active) return;
        setValidInfo(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await completeMarketplacePasswordReset({
        token,
        password,
      });
      setSuccess(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Marketplace password reset error:", err);
      }
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const showInvalid = !loading && !validInfo;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#fef3f2,_#f7ebe7_42%,_#eef2ff_100%)]">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg rounded-[28px] border border-white/70 bg-white/85 px-8 py-8 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur">
          {loading ? <div className="text-sm text-slate-600">Loading...</div> : null}

          {showInvalid ? (
            <div className="text-center">
              <h1 className="text-3xl font-semibold text-slate-900">
                This MyBoothShare reset link is invalid or expired.
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Please request a new password reset link for your shared renter
                account.
              </p>
              <div className="mt-5">
                <Link
                  to="/login"
                  className="font-medium text-rose-600 hover:text-rose-700"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : null}

          {!loading && validInfo && success ? (
            <div className="text-center">
              <h1 className="text-3xl font-semibold text-slate-900">
                Password updated
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Sign in to MyBoothShare with your new password.
              </p>
              <div className="mt-5">
                <Link
                  to="/login"
                  className="inline-flex rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : null}

          {!loading && validInfo && !success ? (
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">
                Ellyanna MyBoothShare
              </div>
              <h1 className="mt-5 text-3xl font-semibold leading-tight text-slate-900">
                Create a new password
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Choose a new password for your shared MyBoothShare account.
              </p>
              {validInfo?.name ? (
                <p className="mt-3 text-xs text-slate-500">Account: {validInfo.name}</p>
              ) : null}
              {validInfo?.email_masked ? (
                <p className="text-xs text-slate-500">
                  Email on file: {validInfo.email_masked}
                </p>
              ) : null}

              {error ? (
                <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label
                    htmlFor="marketplace-reset-password-new"
                    className="block text-xs font-medium uppercase tracking-wide text-slate-700"
                  >
                    New password
                  </label>
                  <input
                    id="marketplace-reset-password-new"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="marketplace-reset-password-confirm"
                    className="block text-xs font-medium uppercase tracking-wide text-slate-700"
                  >
                    Confirm password
                  </label>
                  <input
                    id="marketplace-reset-password-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving ? "Saving..." : "Update password"}
                </button>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
